import React, { useEffect, useState } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../App';

const TYPE_LABEL = { residential: 'Жилое', non_residential: 'Нежилое' };

function StudioModal({ studio, properties, onClose, onSave }) {
  const [form, setForm] = useState(studio || {
    number: '', property_id: '', area: '', floor: '', description: '', is_active: 1
  });
  const [err, setErr] = useState('');
  const s = f => setForm(p => ({ ...p, ...f }));

  const save = async () => {
    if (!form.number.trim()) return setErr('Номер студии обязателен');
    if (!form.property_id) return setErr('Выберите помещение');
    try {
      if (studio?.id) await put(`/studios/${studio.id}`, form);
      else await post('/studios', form);
      onSave();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{studio?.id ? 'Редактировать студию' : 'Новая студия'}</div>
        {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}
        <div className="form-grid">
          <div className="form-group">
            <label>Номер *</label>
            <input value={form.number} onChange={e => s({ number: e.target.value })} placeholder="47-1" />
          </div>
          <div className="form-group">
            <label>Площадь (м²)</label>
            <input type="number" value={form.area || ''} onChange={e => s({ area: e.target.value })} placeholder="32" />
          </div>
          <div className="form-group span-2">
            <label>Помещение *</label>
            <select value={form.property_id || ''} onChange={e => s({ property_id: e.target.value })}>
              <option value="">Выберите помещение...</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>
                  {p.number} ({TYPE_LABEL[p.type] || p.type})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Этаж</label>
            <input type="number" value={form.floor || ''} onChange={e => s({ floor: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Статус</label>
            <select value={form.is_active} onChange={e => s({ is_active: parseInt(e.target.value) })}>
              <option value={1}>Активна</option>
              <option value={0}>Неактивна</option>
            </select>
          </div>
          <div className="form-group span-2">
            <label>Описание</label>
            <textarea value={form.description || ''} onChange={e => s({ description: e.target.value })} placeholder="Доп. информация" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={save}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

export default function StudiosPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [studios, setStudios] = useState([]);
  const [properties, setProperties] = useState([]);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');
  const [filterProp, setFilterProp] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const load = () => {
    get('/studios').then(setStudios).catch(() => {});
    get('/properties').then(setProperties).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = studios.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || s.number.toLowerCase().includes(q) ||
      (s.property_number || '').toLowerCase().includes(q) ||
      (s.tenant_name || '').toLowerCase().includes(q);
    const matchProp = !filterProp || String(s.property_id) === filterProp;
    const matchStatus = filterStatus === 'all' ||
      (filterStatus === 'busy' && s.active_contract_id) ||
      (filterStatus === 'free' && !s.active_contract_id);
    return matchSearch && matchProp && matchStatus;
  });

  const freeCount = studios.filter(s => !s.active_contract_id).length;
  const busyCount = studios.filter(s => s.active_contract_id).length;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          Студии
          <span>{studios.length} объектов</span>
        </h1>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setModal({})}>+ Добавить студию</button>
        )}
      </div>

      {/* Quick stats */}
      <div className="stat-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-label">Всего студий</div>
          <div className="stat-value blue">{studios.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Занято</div>
          <div className="stat-value green">{busyCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Свободно</div>
          <div className="stat-value">{freeCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Загруженность</div>
          <div className="stat-value">
            {studios.length ? Math.round(busyCount / studios.length * 100) : 0}%
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          placeholder="Поиск по номеру, помещению, арендатору..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <select value={filterProp} onChange={e => setFilterProp(e.target.value)}>
          <option value="">Все помещения</option>
          {properties.map(p => (
            <option key={p.id} value={p.id}>{p.number}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">Все статусы</option>
          <option value="busy">Занятые</option>
          <option value="free">Свободные</option>
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Студия</th>
                <th>Помещение</th>
                <th>Тип</th>
                <th>Площадь</th>
                <th>Арендатор</th>
                <th>Ставка / мес</th>
                <th>Статус</th>
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>
                    Студий не найдено
                  </td>
                </tr>
              )}
              {filtered.map(s => (
                <tr key={s.id}>
                  <td><strong>{s.number}</strong></td>
                  <td style={{ color: 'var(--text2)' }}>{s.property_number || '—'}</td>
                  <td>
                    {s.property_type && (
                      <span className={`badge ${s.property_type === 'residential' ? 'badge-blue' : 'badge-yellow'}`}>
                        {TYPE_LABEL[s.property_type]}
                      </span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text2)' }}>{s.area ? `${s.area} м²` : '—'}</td>
                  <td>
                    {s.tenant_name
                      ? <span style={{ fontWeight: 600 }}>{s.tenant_name}</span>
                      : <span style={{ color: 'var(--text3)' }}>Свободна</span>
                    }
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    {s.current_rate ? `${new Intl.NumberFormat('ru-RU').format(s.current_rate)} ₽` : '—'}
                  </td>
                  <td>
                    <span className={`badge ${s.active_contract_id ? 'badge-green' : 'badge-gray'}`}>
                      {s.active_contract_id ? 'Занята' : 'Свободна'}
                    </span>
                  </td>
                  {isAdmin && (
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => setModal(s)}>✏️</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal !== null && (
        <StudioModal
          studio={modal.id ? modal : null}
          properties={properties}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
